import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  ChipProps,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
  CircularProgress
} from "@mui/material";
import axios from "axios";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { OrderInfo } from "../../shared/orderInfo/types";
import OrderDetails from "../OrderDetails";
import { FundingPackage, FundingPackageStatus, FundingPackageStatusConfig, FundingPriority } from "../types";
import UploadFunding from "./UploadFunding";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import UploadModal from "../../shared/UploadModal";
import { FundingAuthorizationContext } from "../FundingAuthorizationContext";
import NotesSection from "../NotesSection";
import { useDropzone } from "react-dropzone";
import { toBase64 } from "../../utils/helpers";
import { stripDataUriPrefix } from "../../utils/stripDataUriPrefix";
import WiredIFrame from "../WiredIframe";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { FundingPackageLockMode } from "../types";
import { useFundingPackageLock } from "../useFundingPackageLock";
import FundingPackageLockBanner from "../FundingPackageLockBanner";

export default function NewFundingPackageDetails() {
  const { compositeId } = useParams<{ compositeId: string }>();
  const [orderNumber, rawPackageNumber] = compositeId?.split("-pkg") ?? [];
  const packageNumber = Number(rawPackageNumber ?? 1);
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
  const navigate = useNavigate();
  const { fetchFundingPackages } = useContext(FundingAuthorizationContext);
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [fundingPackageInfo, setFundingPackageInfo] = useState<FundingPackage>();
  const [urgency, setUrgency] = useState<FundingPriority | "">(fundingPackageInfo?.priority ?? "");
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [invalidAuthIds, setInvalidAuthIds] = useState<number[]>([]);
  const [sourcesAndUsesInvalid, setSourcesAndUsesInvalid] = useState(false);
  const [uploading, setUploading] = useState(false);
  const sortedSourcesAndUsesPdfs = useMemo(
    () => [...(fundingPackageInfo?.sourcesAndUsesPdfs ?? [])].sort((a, b) => a.documentNumber - b.documentNumber),
    [fundingPackageInfo?.sourcesAndUsesPdfs]
  );
  const packageLock = useFundingPackageLock(fundingPackageInfo?.id, FundingPackageLockMode.Edit);

  const documentInfo = {
    description: `Funding Package ${fundingPackageInfo?.packageNumber !== 1 ? fundingPackageInfo?.packageNumber : ""}`,
    base64File: "",
    fileName: "",
    subcategoryId: "PCLDBS",
    mTANumber: orderInfo?.number,
    applicationInitiator: "TitleQ.FundingPackages",
    documentDescription: "Funding Package"
  };

  const getPackageInfo = async () => {
    const { data } = await axios.get(
      `/api/FundingPackages/packages/GetPackageByOrderAndPackageNumber?orderNumber=${orderNumber}&packageNumber=${packageNumber}`
    );
    setFundingPackageInfo(data);
    if (data.orderNumber) {
      getAndSetOrderInfo(data.orderNumber);
    }
  };
  const getAndSetOrderInfo = async (orderNumber: string) => {
    try {
      const { data } = await axios.get(`/proxy/api/orders/getOrderInfo?orderNumber=${orderNumber}`);
      setOrderInfo(data);
    } catch (error) {
      console.error("Failed to fetch order info:", error);
    }
  };

  const submitCancelRequest = async () => {
    if (!packageLock.isLockedByMe) return;
    await axios.post("/api/FundingPackages/packages/UpdateFundingPackageStatus", {
      fundingPackageId: fundingPackageInfo?.id,
      status: FundingPackageStatus.Cancelled
    });
    await fetchFundingPackages();
    navigate("/funding-packages");
  };

  const handleSubmitForReview = async () => {
    if (!packageLock.isLockedByMe) return;
    if (!fundingPackageInfo) return;
    if (fundingPackageInfo.status === FundingPackageStatus.Funded) return;
    // const missingDocs = fundingPackageInfo.fundingAuthorizations
    //   .filter((auth: FundingAuthorization) => {
    //     if (auth.momentumDocumentId && auth.momentumDocumentId !== 0) return false;
    //     if (auth.type === AuthorizationType.Lender) return auth.isAuthorizationRequired;
    //     return true;
    //   })
    //   .map((auth) => auth.id);

    // if (missingDocs.length > 0) {
    //   setInvalidAuthIds(missingDocs);
    //   return;
    // }
    const hasSourcesAndUses = fundingPackageInfo.sourcesAndUsesPdfs?.some(
      (s) => s.momentumDocumentId != null && s.momentumDocumentId > 0
    );
    if (!hasSourcesAndUses) {
      setSourcesAndUsesInvalid(true);
      return;
    }
    setSourcesAndUsesInvalid(false);
    await axios.post(
      `/api/FundingPackages/packages/SubmitPackageForReview?id=${fundingPackageInfo?.id}&priority=${urgency}`
    );
    await fetchFundingPackages();
    navigate("/funding-packages");
  };

  const handleSaveDraft = async () => {
    if (!packageLock.isLockedByMe) return;
    await axios.post("/api/FundingPackages/packages/UpdateFundingPackagePriority", {
      fundingPackageId: fundingPackageInfo?.id,
      priority: urgency
    });
    getPackageInfo();
    await fetchFundingPackages();
    navigate("/funding-packages");
  };

  const handleSaveSourcesAndUses = async (momentumDocIds: number[]) => {
    if (!packageLock.isLockedByMe) return;
    const fundingPackageId = fundingPackageInfo?.id;
    if (momentumDocIds) {
      await axios.post(
        `/api/FundingPackages/packages/AddSourcesAndUsesPdfs?fundingPackageId=${fundingPackageId}`,
        momentumDocIds
      );
    }
    getPackageInfo();
  };
  const handleDelete = async (sourcesAndUsesId: number | undefined) => {
    if (!packageLock.isLockedByMe) return;
    await axios.post(`/api/FundingPackages/packages/RemoveSourcesAndUsesDoc?id=${sourcesAndUsesId}`);
    getPackageInfo();
  };
  const uploadSourcesAndUses = async (files: File[]) => {
    if (!packageLock.isLockedByMe) return;
    if (!fundingPackageInfo) return;
    const fileUploads = await Promise.all(
      files.map(async (file) => ({
        base64File: stripDataUriPrefix(await toBase64(file)),
        fileName: file.name
      }))
    );
    await axios.post("/api/FundingPackages/packages/UploadSourcesAndUsesPdfs", {
      fundingPackageId: fundingPackageInfo.id,
      files: fileUploads
    });
    getPackageInfo();
  };

  const handleFileDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length || !fundingPackageInfo || uploading) return;
      setUploading(true);
      try {
        await uploadSourcesAndUses(acceptedFiles);
      } finally {
        setUploading(false);
      }
    },
    [fundingPackageInfo, uploading]
  );
  const handleSourcesAndUsesDragEnd = async (result: DropResult) => {
    if (!packageLock.isLockedByMe) return;
    if (!fundingPackageInfo || !result.destination || result.destination.index === result.source.index) return;

    const reorderedPdfs = [...sortedSourcesAndUsesPdfs];
    const [movedPdf] = reorderedPdfs.splice(result.source.index, 1);
    reorderedPdfs.splice(result.destination.index, 0, movedPdf);

    const renumberedPdfs = reorderedPdfs.map((pdf, index) => ({
      ...pdf,
      documentNumber: index + 1
    }));

    setFundingPackageInfo({
      ...fundingPackageInfo,
      sourcesAndUsesPdfs: renumberedPdfs
    });

    try {
      await axios.post("/api/FundingPackages/packages/ReorderSourcesAndUsesPdfs", {
        fundingPackageId: fundingPackageInfo.id,
        sourcesAndUsesPdfIds: renumberedPdfs.map((pdf) => pdf.id)
      });
      getPackageInfo();
    } catch (error) {
      getPackageInfo();
      console.error("Failed to reorder funding package uploads:", error);
    }
  };

  useEffect(() => {
    getPackageInfo();
  }, [fundingPackageInfo?.id]);

  useEffect(() => {
    if (fundingPackageInfo?.priority != null) {
      setUrgency(fundingPackageInfo.priority);
    }
  }, [fundingPackageInfo?.priority]);

  useEffect(() => {
    if (!orderNumber) return;
    if (!rawPackageNumber) {
      navigate(`/edit-funding-package/${orderNumber}-pkg1`, { replace: true });
    }
  }, [orderNumber, rawPackageNumber, navigate]);

  const status = fundingPackageInfo?.status ?? FundingPackageStatus.Draft;
  const config = FundingPackageStatusConfig[status];
  const isFunded = status === FundingPackageStatus.Funded;
  const [isCondensed, setIsCondensed] = useState<boolean>(false);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop,
    multiple: true,
    noClick: true,
    disabled: !packageLock.isLockedByMe
  });

  return (
    <Stack position={"relative"} margin="0% auto" sx={{ display: "flex", justifyContent: "center", maxWidth: "90%" }}>
      <Stack margin="0% auto">
      <Typography variant="h4">New Funding Package</Typography>
      <FundingPackageLockBanner
        lock={packageLock.lock}
        isLockedByMe={packageLock.isLockedByMe}
        isLockedByOtherUser={packageLock.isLockedByOtherUser}
        hasIncomingTakeoverRequest={packageLock.hasIncomingTakeoverRequest}
        takeoverRequestedByName={packageLock.takeoverRequestedByName}
        secondsUntilTakeoverAllowed={packageLock.secondsUntilTakeoverAllowed}
        onRequestTakeover={packageLock.requestTakeover}
        onAcceptTakeover={packageLock.acceptTakeover}
        onDeclineTakeover={packageLock.declineTakeover}
        onForceTakeover={packageLock.forceTakeover}
        onRelease={packageLock.releaseLock}
      />
      <Chip
        label={config.label}
        color={config.color}
        sx={{
          position: "absolute",
          top: -30,
          right: 0,
          fontWeight: 600,
          zIndex: 1300
        }}
      />
      <Stack direction="row" spacing={2} justifyContent="space-between" marginTop={2}>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ maxWidth: 680 }}>
            <OrderDetails orderInfo={orderInfo} isCondensed={isCondensed} />
          </Stack>
          <WiredIFrame orderNumber={orderNumber}/>
          <NotesSection fundingPackageId={fundingPackageInfo?.id} />
        </Stack>
        <Stack direction="column" spacing={2} justifyContent="space-between">
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ maxWidth: 900 }}>
            {fundingPackageInfo?.fundingAuthorizations.map((authorization, index) => (
              <Box key={authorization.id || index}>
                <UploadFunding
                  orderNumber={orderInfo?.number}
                  fundingAuthorization={authorization}
                  refreshData={getPackageInfo}
                  isInvalid={invalidAuthIds.includes(authorization.id)}
                  isResidential={orderInfo?.propertyUse === "Residential"}
                  fundingPackage={fundingPackageInfo}
                  disabled={!packageLock.isLockedByMe}
                />
              </Box>
            ))}
          </Stack>
          <Card sx={{ p: 2, flex: 1, maxWidth: 680 }}>
            <Typography fontWeight={600} mb={1}>
              Funding Package
            </Typography>
            <DragDropContext onDragEnd={handleSourcesAndUsesDragEnd}>
              <Droppable droppableId="funding-package-uploads">
                {(provided) => (
                  <Stack ref={provided.innerRef} {...provided.droppableProps} spacing={1} mb={sortedSourcesAndUsesPdfs.length ? 2 : 0}>
                    {sortedSourcesAndUsesPdfs.map((pdf, index) => (
                      <Draggable
                        key={pdf.id}
                        draggableId={`funding-package-upload-${pdf.id}`}
                        index={index}
                        isDragDisabled={sortedSourcesAndUsesPdfs.length < 2 || !packageLock.isLockedByMe}
                      >
                        {(provided, snapshot) => (
                          <Stack
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            sx={{
                              py: 0.25,
                              backgroundColor: snapshot.isDragging ? "action.hover" : "transparent",
                              boxShadow: snapshot.isDragging ? 1 : 0
                            }}
                          >
                            <IconButton
                              size="small"
                              {...provided.dragHandleProps}
                              disabled={sortedSourcesAndUsesPdfs.length < 2 || !packageLock.isLockedByMe}
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </IconButton>
                            <Link
                              target="_blank"
                              underline="hover"
                              href={`${momentumUrl}opendoc?id=${pdf.momentumDocumentId}`}
                              sx={{ flex: 1, minWidth: 0 }}
                            >
                              {pdf.momentumDocumentName}
                            </Link>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDelete(pdf.id)}
                              disabled={!packageLock.isLockedByMe}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
            <Box
              {...getRootProps()}
              onClick={() => setOpenUploadModal(true)}
              sx={{
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: uploading || !packageLock.isLockedByMe ? "default" : "pointer",
                bgcolor: "transparent",
                height: 200,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed",
                borderColor: sourcesAndUsesInvalid ? " #f44336" : "grey.300",
                backgroundColor: sourcesAndUsesInvalid ? "rgba(244,67,54,0.08)" : "none",
                pointerEvents: uploading || !packageLock.isLockedByMe ? "none" : "auto",
                ...(isDragActive && {
                  boxShadow: "0 0 0 2px rgba(25,118,210,0.35)"
                })
              }}
            >
              {uploading ? (
                <>
                  <CircularProgress size={32} />
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Uploading...
                  </Typography>
                </>
              ) : (
                <>
                  <UploadIcon color="action" />
                  <input {...getInputProps()} />
                  <Typography variant="body2" color="text.secondary">
                    Drag & drop files here or click to upload
                  </Typography>
                </>
              )}
            </Box>
          </Card>
          <Card sx={{ p: 2, maxWidth: 500, height: 100, alignSelf: "flex-end" }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography fontWeight={600}>Funding Urgency</Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={urgency}
                  onChange={(e) => setUrgency(Number(e.target.value) as FundingPriority)}
                  displayEmpty
                  disabled={!packageLock.isLockedByMe}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  <MenuItem value={FundingPriority.PayoffCutoff}>Payoff Cutoff</MenuItem>
                  <MenuItem value={FundingPriority.Now}>Now</MenuItem>
                  <MenuItem value={FundingPriority.Today}>Today</MenuItem>
                  <MenuItem value={FundingPriority.Soon}>Soon</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Card>
        </Stack>
      </Stack>
      {/* {invalidAuthIds.length > 0 && (
  <Alert severity="error" sx={{ mb: 2 }}>
    All authorizations must have a document uploaded before submitting for review.
  </Alert>
)} */}
      {openUploadModal && (
        <UploadModal
          updateItems={() => {}}
          open={openUploadModal}
          handleClose={() => setOpenUploadModal(false)}
          hasDocumentType={false}
          hasAdditionalDescription={false}
          validationCheck={false}
          error={""}
          onSave={handleSaveSourcesAndUses}
          documentInfo={documentInfo}
          orderNumber={orderInfo?.number ? orderInfo?.number : ""}
          isUseNameOfFile={false}
          applicationInitiator="TitleQ.FundingPackages"
          allowMultiple
          onSaveFiles={uploadSourcesAndUses}
        />
      )}
      <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", gap: 2, marginTop: 8 }}>
        <Button
          variant="outlined"
          sx={{
            color: "red",
            borderColor: "red",
            "&:hover": { borderColor: "red", backgroundColor: "#f5f5f5" }
          }}
          onClick={submitCancelRequest}
          disabled={!packageLock.isLockedByMe}
        >
          Cancel Request
        </Button>
        <Button
          variant="outlined"
          sx={{
            color: "black",
            borderColor: "black",
            "&:hover": { borderColor: "black", backgroundColor: "#f5f5f5" }
          }}
          onClick={handleSaveDraft}
          disabled={!packageLock.isLockedByMe}
        >
          Save Draft
        </Button>

        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowForwardIcon />}
          sx={{
            backgroundColor: "purple",
            "&:hover": { backgroundColor: "#6a0dad" }
          }}
          onClick={handleSubmitForReview}
          disabled={!packageLock.isLockedByMe || isFunded}
        >
          Submit
        </Button>
      </Box>
      </Stack>
    </Stack>
  );
}
